<script setup lang="ts">
import { nextTick, ref } from 'vue'
import { Activity, CircleUserRound, Crosshair, History, Leaf, Moon, ScrollText, Settings, Sun, Trophy } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import GameIcon from '@/components/GameIcon.vue'
import type { AppLocale } from '@/i18n/messages'
import { preloadRouteView, type PrimaryRouteName } from '@/router'
import { useAppStore } from '@/stores/app'
import { GAME_ICONS } from '@/ui/game-icons'

const { t } = useI18n()
const app = useAppStore()
const localeSwitch = ref<HTMLElement | null>(null)
const locales: readonly { value: AppLocale; label: string; short: string; mark: string; lang: AppLocale; name: string }[] = [
  { value: 'zh-CN', label: '中文', short: '中', mark: '文', lang: 'zh-CN', name: '中文' },
  { value: 'en', label: 'English', short: 'EN', mark: 'A', lang: 'en', name: 'English' },
]
const items: readonly { to: string; name: PrimaryRouteName; label: string; icon: typeof Activity; mobileSecondary?: boolean }[] = [
  { to: '/', name: 'live', label: 'nav.dashboard', icon: Activity },
  { to: '/calibration', name: 'calibration', label: 'nav.calibration', icon: Crosshair },
  { to: '/history', name: 'history', label: 'nav.history', icon: History },
  { to: '/rank', name: 'rank', label: 'nav.rank', icon: Trophy },
  { to: '/changelog', name: 'changelog', label: 'nav.changelog', icon: ScrollText, mobileSecondary: true },
  { to: '/settings', name: 'settings', label: 'nav.settings', icon: Settings },
  { to: '/about', name: 'about', label: 'nav.about', icon: CircleUserRound, mobileSecondary: true },
]

function prepareNavigation(name: PrimaryRouteName): void {
  void preloadRouteView(name)
}

function setLocale(value: AppLocale): void {
  app.locale = value
}

async function onLocaleKeydown(event: KeyboardEvent): Promise<void> {
  if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return
  event.preventDefault()
  const current = locales.findIndex((item) => item.value === app.locale)
  let next = current < 0 ? 0 : current
  if (event.key === 'Home') next = 0
  else if (event.key === 'End') next = locales.length - 1
  else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % locales.length
  else next = (current - 1 + locales.length) % locales.length
  const locale = locales[next]
  if (!locale) return
  setLocale(locale.value)
  await nextTick()
  localeSwitch.value?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus()
}
</script>

<template>
  <aside class="app-sidebar">
    <RouterLink class="brand" to="/" :aria-label="t('app.name')">
      <span class="brand-mark"><GameIcon :src="GAME_ICONS.mapleLeaf" dimension="1.875rem" eager><Leaf stroke-width="2.2" /></GameIcon></span>
      <span class="brand-copy">
        <strong class="brand-name">{{ t('app.name') }}</strong>
        <small class="brand-subtitle">{{ t('app.tagline') }}</small>
      </span>
    </RouterLink>
    <nav class="nav-list" :aria-label="t('app.primaryNav')">
      <RouterLink v-for="item in items" :key="item.to" :to="item.to" class="nav-item" :class="{ 'mobile-secondary': item.mobileSecondary }" :aria-label="item.name === 'rank' ? `${t(item.label)} Beta` : t(item.label)" :title="t(item.label)" @pointerenter="prepareNavigation(item.name)" @pointerdown="prepareNavigation(item.name)" @focus="prepareNavigation(item.name)">
        <span class="nav-icon"><component :is="item.icon" style="width:1.1875rem;height:1.1875rem" stroke-width="1.9" aria-hidden="true" /></span>
        <span class="nav-label">{{ t(item.label) }}<span v-if="item.name === 'rank'" class="nav-beta" aria-hidden="true">Beta</span></span>
      </RouterLink>
    </nav>
    <div class="sidebar-footer">
      <div ref="localeSwitch" class="locale-switch" role="radiogroup" :aria-label="t('app.language')" :data-locale="app.locale" @keydown="onLocaleKeydown">
        <span class="locale-switch-thumb" aria-hidden="true" />
        <button v-for="locale in locales" :key="locale.value" class="locale-option" type="button" role="radio" :lang="locale.lang" :aria-label="locale.name" :aria-checked="app.locale === locale.value" :tabindex="app.locale === locale.value ? 0 : -1" @click="setLocale(locale.value)">
          <span class="locale-option-mark" aria-hidden="true">{{ locale.mark }}</span>
          <span class="locale-option-label locale-option-full">{{ locale.label }}</span>
          <span class="locale-option-label locale-option-short">{{ locale.short }}</span>
        </button>
      </div>
      <button class="theme-toggle" type="button" :title="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" :aria-label="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" @click="app.toggleTheme">
        <Moon v-if="app.theme === 'light'" style="width:1.0625rem;height:1.0625rem" aria-hidden="true" />
        <Sun v-else style="width:1.0625rem;height:1.0625rem" aria-hidden="true" />
        <span>{{ app.theme === 'light' ? t('settings.dark') : t('settings.light') }}</span>
      </button>
    </div>
  </aside>
</template>
